import '@Pages/GeoMMM/Results/NextMediaDollar.scss';
import * as R from 'ramda';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, FC } from 'react';
import { Column, DimensionColumn } from '@blisspointmedia/bpm-types/dist/MetricsTable';
import { currencyFormatter, snapShotDataSort } from '@blisspointmedia/bpm-types/dist/MMMUtils';
import { ChartContainer } from '@Components/ChartContainer';
import { DownloadDropdown } from '@Components/DownloadDropdown';
import { InfoTooltip } from '@Components/InfoTooltip/InfoTooltip';
import { MetricsTable } from '@Components/MetricsTable/MetricsTable';
import { WidgetContainer } from '@Components/WidgetContainer';
import {
  formatROITableData,
  formatSnapshotData,
  hasNextDollarMarginalCpa
} from '@Pages/GeoMMM/DataFormatters';
import { SnapshotChartWithCI } from '@Pages/GeoMMM/Results/Components/SnapshotChartWithCI';
import { Size } from '@Components/designConstants';
import { exportToExcel } from '../../../utils/download-utils';
import { useCompanyInfo } from '../../../utils/hooks/useCompanyInfo';

const TABLE_HEADERS: Column[] = [
  {
    id: 'previousQuarterROILow',
    dataVarName: 'roi',
    label: 'Previous Quarter ROAS Low'
  },
  {
    id: 'previousQuarterROI',
    dataVarName: 'roi',
    label: 'Previous Quarter ROAS'
  },
  {
    id: 'previousQuarterROIHigh',
    dataVarName: 'roi',
    label: 'Previous Quarter ROAS High'
  },
  {
    id: 'entireTimePeriodROILow',
    dataVarName: 'roi',
    label: 'Entire Time Period ROAS Low'
  },
  {
    id: 'entireTimePeriodROI',
    dataVarName: 'roi',
    label: 'Entire Time Period ROAS'
  },
  {
    id: 'entireTimePeriodROIHigh',
    dataVarName: 'roi',
    label: 'Entire Time Period ROAS High'
  },
  {
    id: 'previousQuarterMedianSpend',
    dataVarName: 'spend',
    label: 'Previous Quarter Mean Spend'
  },
  {
    id: 'predictedResponseToMedianSpendLow',
    dataVarName: 'response',
    label: 'Predicted Response To Mean Spend Low'
  },
  {
    id: 'predictedResponseToMedianSpend',
    dataVarName: 'response',
    label: 'Predicted Response To Mean Spend'
  },
  {
    id: 'predictedResponseToMedianSpendHigh',
    dataVarName: 'response',
    label: 'Predicted Response To Mean Spend High'
  },
  {
    id: 'nextDollarEffectiveness',
    dataVarName: 'nextDollarEffectiveness',
    label: 'Next Best Dollar Effectiveness',
    heatMapping: {
      colorScheme: 'sequential'
    } as any
  }
];

const TABLE_HEIGHT = 360;
const HEADER_TEXT_HEIGHT = 64;

interface NextMediaDollarProps {
  roiTableRows: any[];
  channelBreakoutMap: Record<
    string,
    {
      channel: string;
      platform: string;
      tactic: string;
      brand_nonbrand: string;
    }
  >;
  outcomeVariable: 'revenue' | 'kpiVolume';
  maxDimension: string;
}

export const NextMediaDollar: FC<NextMediaDollarProps> = ({
  roiTableRows,
  channelBreakoutMap,
  outcomeVariable,
  maxDimension
}) => {
  const [snapChartSortValue, setSnapChartSortValue] = useState('Highest to lowest');
  const [, setTableWidth] = useState(500);
  const [rerenderTable, setRerenderTable] = useState(false);
  const containerRef = useRef(null);
  const { cid: company } = useCompanyInfo();

  useEffect(() => {
    const windowResizeFunction = () => setRerenderTable(true);
    window.addEventListener('resize', windowResizeFunction);
    return () => window.removeEventListener('resize', windowResizeFunction);
  }, []);

  const channelBreakoutHeaderLabel = useMemo(() => {
    switch (maxDimension) {
      case 'Platform':
        return 'Channel, Platform';
      case 'Tactic':
        return 'Channel, Platform, Tactic';
      case 'Brand/Non-Brand':
        return 'Channel, Platform, Tactic, Brand/Non-Brand';
      default:
        return 'Channel';
    }
  }, [maxDimension]);

  const DimensionColumns: DimensionColumn[] = useMemo(
    () => [
      {
        id: 'name',
        dimensionVarName: 'Channel',
        label: channelBreakoutHeaderLabel,
        dimensionTypeName: 'Channel',
        divider: false
      }
    ],
    [channelBreakoutHeaderLabel]
  );

  useLayoutEffect(() => {
    const newTableWidth = containerRef.current
      ? (containerRef.current as any).offsetWidth
      : undefined;
    if (newTableWidth) {
      setTableWidth((containerRef.current as any).offsetWidth - 5);
    }
    if (rerenderTable) {
      setRerenderTable(false);
    }
  }, [rerenderTable]);

  // A kpiVolume client's sheet either has Econ's new "Next Dollar Marginal CPA" / bound columns
  // for every channel or none at all yet - fall back to the pre-existing effectiveness framing
  // (label, value, bounds, tooltip, heatmap direction) until Econ re-exports that client.
  const useNewCpaFraming = useMemo(
    () => outcomeVariable === 'kpiVolume' && hasNextDollarMarginalCpa(roiTableRows),
    [outcomeVariable, roiTableRows]
  );

  const { tableHeaders, snapChartData, roiTableData, dimensionData } = useMemo(() => {
    const output = {
      tableHeaders: [] as Column[],
      snapChartData: [] as any[],
      roiTableData: [] as any[],
      dimensionData: [] as any[]
    };

    if (!R.isEmpty(roiTableRows) && !R.isEmpty(R.keys(channelBreakoutMap))) {
      output.snapChartData = snapShotDataSort(
        formatSnapshotData(roiTableRows, outcomeVariable),
        snapChartSortValue
      );

      // Set labels based on outcomeVariable - change ROAS to CPA for kpiVolume
      output.tableHeaders = TABLE_HEADERS.map(item => {
        if (item.label && item.label.includes('ROAS')) {
          const roiType = outcomeVariable === 'revenue' ? 'ROAS' : 'CPA';
          return {
            ...item,
            label: item.label.replace('ROAS', roiType)
          };
        }
        if (item.id === 'nextDollarEffectiveness' && useNewCpaFraming) {
          return {
            ...item,
            label: 'Next Dollar Marginal CPA',
            // Lower CPA is better, unlike effectiveness (higher is better) - see
            // MetaFatigueInsights.tsx's CPX/CPC/CPM heatMapping entries for the same pattern.
            heatMapping: {
              ...item.heatMapping,
              minIsBest: true
            } as any
          };
        }
        return item;
      });

      // Format raw sheet row data to match rMMM data
      const combinedDataMap: Record<string, any> = {};
      roiTableRows.forEach(row => {
        const {
          channel: breakout,
          'Lower Bound (Marg)': ciLowEffectiveness,
          'Upper Bound (Marg)': ciHighEffectiveness,
          'Lower Bound (Marg CPA)': ciLowMarginalCPA,
          'Upper Bound (Marg CPA)': ciHighMarginalCPA,
          'Lower Bound (LQ)': previousQuarterROILow,
          'Mean (LQ)': previousQuarterROI,
          'Upper Bound (LQ)': previousQuarterROIHigh,
          'Lower Bound': entireTimePeriodROILow,
          Mean: entireTimePeriodROI,
          'Upper Bound': entireTimePeriodROIHigh,
          median_spend_lq: previousQuarterMedianSpend,
          pred_median_low: predictedResponseToMedianSpendLow,
          pred_median: predictedResponseToMedianSpend,
          pred_median_high: predictedResponseToMedianSpendHigh,
          'Next Best Dollar Effectiveness': nextDollarEffectiveness,
          'Next Dollar Marginal CPA': nextDollarMarginalCPA
        } = row;

        // For kpiVolume clients whose sheet has been re-exported by Econ, the Meridian pipeline
        // provides the already-reciprocal, already-correctly-labeled CPA columns directly - read
        // them by name rather than computing a reciprocal of the effectiveness columns ourselves.
        // Clients not yet re-exported (no new columns present) fall back to the original columns.
        const ciLow = useNewCpaFraming ? ciLowMarginalCPA : ciLowEffectiveness;
        const ciHigh = useNewCpaFraming ? ciHighMarginalCPA : ciHighEffectiveness;
        const nextDollarValue = useNewCpaFraming ? nextDollarMarginalCPA : nextDollarEffectiveness;

        if (channelBreakoutMap[breakout.toLowerCase()]) {
          const { channel, platform, tactic, brand_nonbrand } =
            channelBreakoutMap[breakout.toLowerCase()];
          combinedDataMap[breakout] = {
            name: breakout,
            channel,
            platform,
            tactic,
            brand_nonbrand,
            ciLow,
            ciHigh,
            previousQuarterROILow,
            previousQuarterROI,
            previousQuarterROIHigh,
            entireTimePeriodROILow,
            entireTimePeriodROI,
            entireTimePeriodROIHigh,
            previousQuarterMedianSpend,
            predictedResponseToMedianSpendLow,
            predictedResponseToMedianSpend,
            predictedResponseToMedianSpendHigh,
            nextDollarEffectiveness: nextDollarValue
          };
        }
      });

      output.roiTableData = formatROITableData({
        data: R.values(combinedDataMap),
        tableHeaders: output.tableHeaders,
        outcomeVariable
      });
      output.dimensionData = formatROITableData({
        data: R.values(combinedDataMap),
        tableHeaders: DimensionColumns,
        outcomeVariable
      });
    }

    return output;
  }, [
    channelBreakoutMap,
    roiTableRows,
    snapChartSortValue,
    outcomeVariable,
    DimensionColumns,
    useNewCpaFraming
  ]);

  const excelDownloadROI = useCallback(() => {
    // Filter out __EMPTY fields that can cause Excel export issues
    const cleanData = roiTableRows.map(row => {
      const cleanRow = { ...row };
      delete cleanRow.__EMPTY;
      return cleanRow;
    });
    exportToExcel(cleanData, `${company}_${outcomeVariable === 'revenue' ? 'ROAS' : 'CPA'}`);
  }, [company, outcomeVariable, roiTableRows]);

  return (
    <WidgetContainer
      collapsible
      header='Where should we allocate our next media dollar?'
      subHeader={
        <>
          Below is the <i className='marginal-emphasis'>marginal</i> impact of each media dollar,
          given the existing allocation of resources.
          <InfoTooltip rightLabel='More info' size={Size.SM}>
            The marginal impact of a media dollar is based on where one is on the channel saturation
            curve (see section below). Next dollar effectiveness is the slope of the line tangent to
            the saturation curve at the current level of investment. Intuitively, you want to invest
            where the slope of the curve is steepest, i.e. offers the highest return.
          </InfoTooltip>
        </>
      }
    >
      <div className='next-media-dollar-section'>
        <div className='left'>
          {!R.isEmpty(snapChartData) && (
            <SnapshotChartWithCI
              title='Estimated Return on Next Invested Dollar'
              data={snapChartData}
              dropdownOptions={[
                { value: 'Alphabetical' },
                { value: 'Highest to lowest' },
                { value: 'Lowest to highest' }
              ]}
              dropdownValue={snapChartSortValue}
              setDropdownValue={setSnapChartSortValue}
              valueFormatter={value => (value ? currencyFormatter.format(value) : '--')}
              pngDownloadClassName='.next-media-dollar-section .left'
            />
          )}
        </div>
        <div className='right'>
          <ChartContainer
            title={`${outcomeVariable === 'revenue' ? 'ROAS' : 'CPA'} and Next Best Dollar`}
            subHeader='Marginal returns reflect the steepness of the saturation curve at current investment levels'
            rightActions={
              <DownloadDropdown menuOptions={['XLSX']} onClickOptions={[excelDownloadROI]} />
            }
          >
            <div ref={containerRef} style={{ width: '100%' }}>
              {!R.isEmpty(roiTableData) &&
                !R.isEmpty(dimensionData) &&
                !R.isEmpty(tableHeaders) &&
                !rerenderTable && (
                  <MetricsTable
                    aggregateData={[]}
                    dimensionColumns={DimensionColumns}
                    dimensionData={dimensionData}
                    tableData={roiTableData}
                    dataColumns={tableHeaders}
                    overrideParams={{
                      headerTextHeight: HEADER_TEXT_HEIGHT
                    }}
                    maxHeight={TABLE_HEIGHT}
                    columnMetaDataMap={{
                      nextDollarEffectiveness: useNewCpaFraming
                        ? {
                            prettyName: 'Next Dollar Marginal CPA',
                            overlayText:
                              'The estimated cost of the next unit of volume (e.g. lead or conversion) for the channel, based on the current level of investment. Lower Bound (Marg CPA) and Upper Bound (Marg CPA) reflect the range of that estimate.',
                            overlayPlacement: 'left'
                          }
                        : {
                            prettyName: 'Next Dollar Effectiveness',
                            overlayText:
                              'Formally, this is the slope of the line tangent to the response\nsaturation curve at the current level of investment for the channel.',
                            overlayPlacement: 'left'
                          }
                    }}
                  />
                )}
            </div>
          </ChartContainer>
        </div>
      </div>
    </WidgetContainer>
  );
};
